<template>
    <VForm
        v-slot="validation"
        class="flex flex-col gap-y-6"
        as="div">
        <div class="text-sm">
            Jahr: {{ staticStore.activeYear?.id }}
        </div>

        <BaseInput
            v-model="statisticProcess.name"
            :label="$t('statisticProcesses.name')"
            :name="$t('statisticProcesses.name')" />

        <BaseSelect
            v-model="selectedStatisticSurvey.id"
            :label="$t('statisticProcesses.statisticSurvey')"
            :name="$t('statisticProcesses.statisticSurvey')"
            :options="statisticSurveys"
            identifier="id"
            rules="required"
            required
            @update:entire-object="selectedStatisticSurvey = $event" />

        <BaseSelect
            v-show="statisticSurveyPeriods.length > 0"
            v-model="statisticProcess.statistic_survey_period_id"
            :label="$t('statisticProcesses.statisticSurveyPeriod')"
            :name="$t('statisticProcesses.statisticSurveyPeriod')"
            :options="statisticSurveyPeriods"
            identifier="id"
            rules="required"
            required>
            <template #selectedOption="option">
                <BaseDate :date="option.start_date" />
                -
                <BaseDate :date="option.end_date" />
                <div class="text-sm text-gray-400">
                    ({{ $t('statisticProcesses.statisticSurveyRecordingPeriod') }}:
                    <BaseDate :date="option.recording_start_date" />
                    -
                    <BaseDate :date="option.recording_end_date" />
                    )
                </div>
            </template>
            <template #option="{option}">
                <BaseDate :date="option.start_date" />
                -
                <BaseDate :date="option.end_date" />
                <div class="text-sm text-gray-400">
                    ({{ $t('statisticProcesses.statisticSurveyRecordingPeriod') }}:
                    <BaseDate :date="option.recording_start_date" />
                    -
                    <BaseDate :date="option.recording_end_date" />
                    )
                </div>
            </template>
        </BaseSelect>

        <BaseSelect
            v-show="statisticForms.length > 0"
            v-model="statisticProcess.statistic_form_id"
            :label="$t('statisticProcesses.statisticForm')"
            :name="$t('statisticProcesses.statisticForm')"
            :options="statisticForms"
            identifier="id"
            rules="required"
            required />

        <BaseSubmitButtons
            :submit-label="$t('statisticProcesses.createStatistic')"
            :loading="loading"
            :validation="validation"
            @submit="validation.handleSubmit(addNewStatisticProcess)"
            @cancel="slideOverStore.resetSlideOver()" />
    </VForm>
</template>

<script setup>
import {useNuxtApp} from "nuxt/app"
import {useI18n} from "vue-i18n"
import {useNotification} from '@/composables/useNotification'
import {useSlideOverStore} from "@/store/slideOver"
import {useStatic} from "@/store/static"
import {$lara, $larafetch} from '@/utils/$larafetch'

const slideOverStore = useSlideOverStore()
const staticStore = useStatic()
const router = useRouter()
const {t: $t} = useI18n()
const notification = useNotification()

const loading = ref(false)
const statisticSurveys = ref([])
const statisticSurveyPeriods = ref([])
const statisticForms = ref([])
const selectedStatisticSurvey = ref({})
const statisticProcess = ref({})

onMounted(() => {
    staticStore.getStaticData()
    fetchStatisticSurveys()
})

watch(() => selectedStatisticSurvey.value, () => {
    statisticSurveyPeriods.value = selectedStatisticSurvey.value.statisticSurveyPeriods
    statisticForms.value = selectedStatisticSurvey.value.statisticForms
})

function fetchStatisticSurveys() {
    $larafetch(useNuxtApp().$apiRoute('spa.statistics.statisticSurveys.index'), {
        params: {
            output: 'collectionAll',
            custom: {recordingYear: staticStore.activeYear.id}
        }
    }).then(response => {
        statisticSurveys.value = response.data

        if (slideOverStore.context) {
            if (slideOverStore.context.statisticFormId) {
                statisticSurveys.value.forEach((statisticSurvey) => {
                    statisticSurvey.statisticForms.forEach((statisticForm) => {
                        if (statisticForm.id === slideOverStore.context.statisticFormId) {
                            selectedStatisticSurvey.value = statisticSurvey
                        }
                    })
                })
                statisticProcess.value.statistic_form_id = slideOverStore.context.statisticFormId
            }
            if (slideOverStore.context.statisticSurveyPeriodId) {
                statisticProcess.value.statistic_survey_period_id = slideOverStore.context.statisticSurveyPeriodId
            }

        }
    }).catch(error => {
        const app = useNuxtApp()
        if (error && error.response?._data.message) {
            notification.error(app.$i18n.t('generalRequestError') + '\n' + error.response._data.message)
        } else if (error && error.response?._data) {
            notification.error(app.$i18n.t('generalRequestError') + '\n' + error.response._data)
        } else {
            notification.error(app.$i18n.t('generalRequestError'))
        }
    })
}

function addNewStatisticProcess() {
    loading.value = true

    $lara.post(useNuxtApp().$apiRoute('spa.statistics.statisticProcesses.store'), {
        body: {...statisticProcess.value},
        formDataRequest: true
    }).then((response) => {
        loading.value = false
        notification.success($t('statisticProcesses.createSuccess'))

        router.push({path: useLocalePath()('/statisticProcess/' + response.data.id)})

        slideOverStore.resetSlideOver()
    }).catch((error) => {
        loading.value = false
        console.error(error)
        notification.error($t('statisticProcesses.createError'), error.data?.errors)
    })
}
</script>

